import React from 'react';
import { Clock, Plus, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// One medication can be taken several times a day, each at its own time.
// The schedule lives in medication_plan[].times (array of "HH:mm"); the legacy
// single `time` field is still written as the first time so older readers keep
// working. `frequency` is only a shortcut for pre-filling `times`.
// ---------------------------------------------------------------------------

// How the medication is taken, relative to food or to the time of day.
export const INSTRUCTION_OPTIONS = [
    'Before Food',
    'With Food',
    'After Food',
    'On Empty Stomach',
    'Before Breakfast',
    'After Breakfast',
    'Before Lunch',
    'After Lunch',
    'Before Dinner',
    'After Dinner',
    'Morning',
    'Evening',
    'Bedtime',
    'With Meals',
    'As Needed (PRN)',
];

/**
 * Archived medications are kept on the plan for the audit trail — they are
 * taken off the carers' rounds and out of the active list, never deleted.
 */
export const isArchived = (med) => !!(med && (med.archived || med.archived_at));

export const FREQUENCY_OPTIONS = [
    { value: 'once', label: 'Once daily' },
    { value: 'twice', label: 'Twice daily' },
    { value: 'three', label: 'Three times daily' },
    { value: 'four', label: 'Four times daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'prn', label: 'PRN (as required)' },
    { value: 'custom', label: 'Custom' },
];

export const FREQUENCY_TIMES = {
    once: ['09:00'],
    twice: ['09:00', '21:00'],
    three: ['08:00', '13:00', '20:00'],
    four: ['08:00', '12:00', '16:00', '20:00'],
    weekly: ['09:00'],
    prn: [],
    custom: ['09:00'],
};

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Clean a list of times: keep valid HH:mm only, de-duplicate, sort. */
export const normalizeTimeList = (list) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    list.forEach((t) => {
        const v = String(t || '').slice(0, 5);
        if (TIME_RE.test(v)) seen.add(v);
    });
    return Array.from(seen).sort();
};

/** Times for a saved medication, falling back to the legacy single `time`. */
export const medTimes = (med) => {
    if (!med) return [];
    if (Array.isArray(med.times)) return normalizeTimeList(med.times);
    return normalizeTimeList([med.time]);
};

/** Which frequency preset (if any) a set of times matches. */
export const frequencyFromTimes = (times) => {
    const norm = normalizeTimeList(times);
    if (norm.length === 0) return 'prn';
    const match = Object.keys(FREQUENCY_TIMES).find((key) => {
        const preset = normalizeTimeList(FREQUENCY_TIMES[key]);
        return preset.length === norm.length && preset.every((t, i) => t === norm[i]);
    });
    return match || 'custom';
};

/** The plan fields that describe when a medication is taken. */
export const scheduleFields = (times) => {
    const norm = normalizeTimeList(times);
    return { times: norm, time: norm[0] || '', frequency: frequencyFromTimes(norm) };
};

/**
 * Administration-times editor: one row per dose time, add/remove freely.
 * `onChange` receives the new list of times.
 */
// Offered in order when a time is added, so a second dose lands on a sensible
// hour instead of duplicating the first one (which the form then rejects).
const SUGGESTED_TIMES = ['09:00', '13:00', '17:00', '21:00', '08:00', '12:00', '16:00', '20:00'];

export const TimesEditor = ({ times = [], onChange, title = 'Administration times' }) => {
    const setAt = (index, value) => onChange(times.map((t, i) => (i === index ? value : t)));
    const removeAt = (index) => onChange(times.filter((_, i) => i !== index));
    const add = () => onChange([...times, SUGGESTED_TIMES.find((t) => !times.includes(t)) || '']);

    return (
        <div className="border border-blue-100 dark:border-gray-700 rounded-xl p-3.5 bg-blue-50/30 dark:bg-[#20242c]">
            <p className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-2.5">
                <Clock className="w-3.5 h-3.5 text-blue-600" />
                {title}
                <span className="font-normal text-gray-400 dark:text-gray-500">
                    {times.length === 0 ? '— as required, no set time' : `${times.length} dose${times.length === 1 ? '' : 's'} a day`}
                </span>
            </p>
            <div className="flex flex-wrap gap-2">
                {times.map((t, i) => (
                    <div key={`${t}-${i}`}
                        className="flex items-center gap-1 bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-700 rounded-lg pl-2.5 pr-1 py-1">
                        <input type="time" value={t} onChange={(e) => setAt(i, e.target.value)}
                            className="bg-transparent text-[13px] text-gray-700 dark:text-gray-200 outline-none" />
                        <button type="button" onClick={() => removeAt(i)} title="Remove this time"
                            className="p-1 rounded-md text-gray-300 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
                <button type="button" onClick={add}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-blue-300 dark:border-blue-800 text-[12px] font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
                    <Plus className="w-3.5 h-3.5" /> Add time
                </button>
            </div>
        </div>
    );
};
